import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/authAPI';
import { sectionsAPI } from '../services/sectionsAPI';
import { backgroundCheckAPI } from '../services/backgroundCheckAPI';
import { formatAPIError } from '../utils/validation';
import Header from '../components/layout/Header';
import { useNavigate, Link } from 'react-router-dom';
import './styles/Dashboard.css';

const CandidateDashboard = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showMFASettings, setShowMFASettings] = useState(false); // ✅ NEW: MFA modal

  // Profile data state
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Education state
  const [educations, setEducations] = useState([]);
  const [loadingEducation, setLoadingEducation] = useState(true);
  const [educationError, setEducationError] = useState('');

  // Certification state
  const [certifications, setCertifications] = useState([]);
  const [loadingCertification, setLoadingCertification] = useState(true);
  const [certificationError, setCertificationError] = useState('');

  // Background check state
  const [backgroundCheckStatus, setBackgroundCheckStatus] = useState(null);
  const [loadingBackgroundCheck, setLoadingBackgroundCheck] = useState(true);

  // ✅ NEW: MFA Settings State
  const [mfaSettings, setMfaSettings] = useState({
    mfaEnabled: user?.mfaEnabled || false,
    mfaType: user?.mfaType || 'email'
  });
  const [tempSettings, setTempSettings] = useState({ ...mfaSettings });

  // ==================== LOAD PROFILE DATA ====================
 const loadProfileData = useCallback(async () => {
  try {
    setLoadingProfile(true);

    const response = await authAPI.getProfile();
    setProfileData(response.data);

    if (response.data) {
      updateUser(prev => ({
        ...prev,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        email: response.data.account?.email,
        phone: response.data.account?.phone,
      }));
    }
  } catch (error) {
    console.error('❌ Failed to load profile:', error);
    setProfileData(null);
  } finally {
    setLoadingProfile(false);
  }
}, [updateUser]); // ✅ remove user


useEffect(() => {
  loadProfileData();
}, [loadProfileData]);


  // ==================== LOAD EDUCATION DATA ====================
  useEffect(() => {
    const loadEducationData = async () => {
      try {
        setLoadingEducation(true);
        setEducationError('');
        console.log('📚 Loading education data...');
        
        const response = await sectionsAPI.education.getAll();
        setEducations(response.data || []);
        console.log('✅ Education data loaded:', response.data?.length || 0, 'entries');
      } catch (error) {
        console.error('❌ Failed to load education data:', error);
        setEducationError('Failed to load education data');
        setEducations([]);
      } finally {
        setLoadingEducation(false);
      }
    };

    loadEducationData();
  }, []);

  // ==================== LOAD CERTIFICATION DATA ====================
  useEffect(() => {
    const loadCertificationData = async () => {
      try {
        setLoadingCertification(true);
        setCertificationError('');
        
        const response = await sectionsAPI.certification.getAll();
        setCertifications(response.data || []);
      } catch (error) {
        console.error('❌ Failed to load certification data:', error);
        setCertificationError('Failed to load certification data');
        setCertifications([]);
      } finally {
        setLoadingCertification(false);
      }
    };

    loadCertificationData();
  }, []);

  // ==================== LOAD BACKGROUND CHECK STATUS ====================
  useEffect(() => {
  const loadBackgroundCheckStatus = async () => {
    try {
      setLoadingBackgroundCheck(true);
      console.log('🔍 Loading background check status...');

      const result = await backgroundCheckAPI.getCandidateChecks();

      const checksArray = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : [];

      setBackgroundCheckStatus(checksArray.length > 0 ? checksArray : null);
    } catch (error) {
      console.error('❌ Failed to load background check status:', error);
      setBackgroundCheckStatus(null);
    } finally {
      setLoadingBackgroundCheck(false);
    }
  };

  loadBackgroundCheckStatus();
}, []);

// Candidate gives consent
const handleGiveConsent = async (checkId) => {
  try {
    setLoadingBackgroundCheck(true);
    setError('');
    setSuccess('');
    await backgroundCheckAPI.giveConsent(checkId, true);
    setSuccess('Background check consent submitted successfully.');
    setTimeout(() => setSuccess(''), 4000);

    const result = await backgroundCheckAPI.getCandidateChecks();

    const checksArray = Array.isArray(result)
      ? result
      : Array.isArray(result?.data)
      ? result.data
      : [];

    setBackgroundCheckStatus(checksArray.length > 0 ? checksArray : null);
  } catch (error) {
    console.error('❌ Failed to give consent:', error);
    setError(formatAPIError(error));
  } finally {
    setLoadingBackgroundCheck(false);
  }
};


  // Helper for badge styling
  const getBackgroundStatusBadgeClass = (status) => {
    if (!status) return 'status-badge status-pending';
    switch (status) {
      case 'PENDING_CONSENT':
        return 'status-badge status-pending';
      case 'ASSIGNED':
      case 'IN_PROGRESS':
        return 'status-badge status-in-progress';
      case 'COMPLETED':
        return 'status-badge status-completed';
      case 'ON_HOLD':
        return 'status-badge status-on-hold';
      case 'UNABLE_TO_VERIFY':
        return 'status-badge status-rejected';
      default:
        return 'status-badge status-pending';
    }
  };

  const getBackgroundResultBadgeClass = (result) => {
    if (!result) return 'status-badge status-pending';
    switch (result) {
      case 'PASS':
        return 'status-badge status-completed';
      case 'FAIL':
        return 'status-badge status-rejected';
      case 'CONDITIONAL':
        return 'status-badge status-on-hold';
      default:
        return 'status-badge status-pending';
    }
  };

  // MFA settings when user changes
  useEffect(() => {
    if (user) {
      setMfaSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
      setTempSettings({
        mfaEnabled: user.mfaEnabled || false,
        mfaType: user.mfaType || 'email'
      });
    }
  }, [user]);

  // ==================== MFA HANDLERS ====================
  const handleMFAToggle = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const newMfaEnabled = !mfaSettings.mfaEnabled;
      await authAPI.updateMFA({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setMfaSettings({
        mfaEnabled: newMfaEnabled,
        mfaType: tempSettings.mfaType
      });

      setSuccess(
        newMfaEnabled
          ? `🎉 Two-Factor Authentication enabled via ${tempSettings.mfaType}!`
          : '✅ Two-Factor Authentication disabled successfully!'
      );

      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('❌ MFA update failed:', error);
      setError(formatAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaTypeChange = (newType) => {
    setTempSettings(prev => ({ ...prev, mfaType: newType }));
  };


  // ==================== HELPER FUNCTIONS ====================
  const getDisplayName = () => {
    if (loadingProfile) return 'Loading...';
    const firstName = profileData?.firstName || user?.firstName;
    const lastName = profileData?.lastName || user?.lastName;
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return user?.email || 'Candidate';
  };

  const getDisplayEmail = () => {
    return profileData?.account?.email || user?.email || 'Not provided';
  };

  const getDisplayPhone = () => {
    return profileData?.account?.phone || user?.phone || 'Not provided';
  };

  const formatEducationDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  const getEducationDateRange = (education) => {
    const startDate = formatEducationDate(education.startDate);
    if (education.isOngoing) {
      return `${startDate} - Present`;
    }
    const endDate = formatEducationDate(education.endDate);
    return `${startDate} - ${endDate}`;
  };

  const getVerificationBadge = (status) => {
    switch (status) {
      case 'VALIDATED':
        return <span className="verification-badge validated">✓ Verified</span>;
      case 'PENDING':
        return <span className="verification-badge pending">⏳ Pending</span>;
      case 'REJECTED':
        return <span className="verification-badge rejected">✗ Rejected</span>;
      default:
        return <span className="verification-badge not-requested">Not Requested</span>;
    }
  };

  const getVerificationStats = () => {
    const totalEducation = educations.length;
    const totalCertification = certifications.length;
    const verifiedEducation = educations.filter(e => e.verification?.status === 'VALIDATED').length;
    const verifiedCertification = certifications.filter(c => c.verification?.status === 'VALIDATED').length;

    const total = totalEducation + totalCertification;
    const verified = verifiedEducation + verifiedCertification;
    const pending = educations.filter(e => e.verification?.status === 'PENDING').length +
                    certifications.filter(c => c.verification?.status === 'PENDING').length;

    return {
      total,
      verified,
      pending,
      completionPercentage: total > 0 ? Math.round((verified / total) * 100) : 0
    };
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const stats = getVerificationStats();

  // ==================== RENDER ====================
  return (
    <>
      <Header />
      
      {/* Role Indicator Banner */}
      <div style={{
        padding: '12px 20px',
        background: '#e8f5e9',
        borderBottom: '1px solid #ddd',
        textAlign: 'center',
        fontSize: '14px',
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <span style={{ fontSize: '20px' }}>💼</span>
        <span>Candidate Dashboard</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>•</span>
        <span style={{ color: '#666', fontWeight: 'normal' }}>Build your verified professional profile</span>
      </div>

      <div className="dashboard-container" style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* Welcome Section */}
        <div className="dashboard-header" style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>Welcome back, {getDisplayName()}!</h1>
          <p style={{ color: '#666', fontSize: '1rem' }}>Manage your profile and track your verification progress</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
            {success}
          </div>
        )}
        {error && (
          <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        {/* Stats Overview */}
        <div className="dashboard-overview">
          <div className="stats-grid">
            {/* Total Records */}
            <div className="stat-card total">
              <div className="stat-icon">📚</div>
              <div className="stat-content">
                <h3>{stats.total}</h3>
                <p>Total Records</p>
                <small>{educations.length} education, {certifications.length} certifications</small>
              </div>
            </div>

            {/* Verified Records */}
            <div className="stat-card verified">
              <div className="stat-icon">✅</div>
              <div className="stat-content">
                <h3>{stats.verified}</h3>
                <p>Verified Records</p>
                <small>{stats.total > 0 ? `${Math.round((stats.verified / stats.total) * 100)}%` : '0%'} of total</small>
              </div>
            </div>

            {/* Pending Verification */}
            <div className="stat-card pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-content">
                <h3>{stats.pending}</h3>
                <p>Pending Verification</p>
                <small>Awaiting review</small>
              </div>
            </div>

            {/* Profile Completion */}
            <div className="stat-card completion">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <h3>{stats.completionPercentage}%</h3>
                <p>Profile Complete</p>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${stats.completionPercentage}%` }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dashboard-section">
  <div className="dashboard-section-header">
    <h2>Search and Apply for jobs</h2>
    <button
  className="btn-primary"
  onClick={() => navigate("/jobs")}
>
  Go to Jobs Feed
</button>
<button className="btn-primary" onClick={() => navigate('/my-applications')}>My Applications</button>
  </div>
  </div>


        {/* Main Dashboard Content */}
        <div className="dashboard-content">
          
          {/* Main Content Area */}
          <div className="dashboard-main">
            
            {/* Education Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>📚 Education</h2>
                <Link to="/candidate/sections" className='card-header-btn' style={{ 
                  padding: '0.5rem 1rem',
                  
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  + Add Education
                </Link>
              </div>
              
              {loadingEducation ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading education data...</p>
                </div>
              ) : educationError ? (
                <div className="error-state">
                  <p>{educationError}</p>
                  <p>Unable to load education data</p>
                </div>
              ) : educations.length > 0 ? (
                <div className="education-list">
                  {educations.slice(0, 3).map(education => (
                    <div key={education.id} className="education-item">
                      <div className="education-info">
                        <h4>{education.degree || 'Degree not specified'}</h4>
                        <p className="education-institution">{education.institution}</p>
                        <small className="education-dates">{getEducationDateRange(education)}</small>
                      </div>
                      {getVerificationBadge(education.verification?.status)}
                    </div>
                  ))}
                  {educations.length > 3 && (
                    <div className="show-more">
                      <Link to="/candidate/sections" className="btn-link">
                        View all {educations.length} education records →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No education added yet</p>
                  <Link to="/candidate/sections" className="btn-link">Add Education</Link>
                </div>
              )}
            </div>

            {/* Certification Section */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>🏆 Certifications</h2>
                <Link to="/candidate/sections" className='card-header-btn' style={{ 
                  padding: '0.5rem 1rem',
                  color: 'white',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>
                  + Add Certification
                </Link>
              </div>
              
              {loadingCertification ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading certifications...</p>
                </div>
              ) : certificationError ? (
                <div className="error-state">
                  <p>{certificationError}</p>
                  <p>Unable to load certification data</p>
                </div>
              ) : certifications.length > 0 ? (
                <div className="certification-list">
                  {certifications.slice(0, 3).map(certification => (
                    <div key={certification.id} className="certification-item">
                      <div className="certification-info">
                        <h4>{certification.name || 'Certification not specified'}</h4>
                        <p className="certification-issuer">{certification.issuer || 'No issuer specified'}</p>
                        <small className="certification-date">
                          {certification.isPursuing ? 'Currently Pursuing' : 
                           certification.issuedAt ? `Issued ${formatEducationDate(certification.issuedAt)}` : 
                           'Date not specified'}
                        </small>
                      </div>
                      {getVerificationBadge(certification.verification?.status)}
                    </div>
                  ))}
                  {certifications.length > 3 && (
                    <div className="show-more">
                      <Link to="/candidate/sections" className="btn-link">
                        View all {certifications.length} certifications →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state">
                  <p>No certifications added yet</p>
                  <Link to="/candidate/sections" className="btn-link">Add Certification</Link>
                </div>
              )}
            </div>

            {/* Background Check Status Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h2>🔍 Background Verification</h2>
              </div>
              <p className="dashboard-card-subtitle">
                Track employer-requested background checks
              </p>
              <div style={{ padding: '1.5rem' }}>
                {loadingBackgroundCheck ? (
                <p>Loading background check status...</p>
              ) : !backgroundCheckStatus ? (
                <div className="empty-state">
                  <p>No background verification requested yet</p>
                  <p>
                    Background checks are typically requested by employers
                    during the hiring process.
                  </p>
                </div>
              ) : (
                <div className="background-check-list">
                  {backgroundCheckStatus.map((check) => (
                    <div
                      key={check.id}
                      className="background-check-item"
                    >
                      <div className="background-check-main">
                        <p className="background-check-label">
                          Employer:{' '}
                          <strong>
                            {check.employer?.companyName ||
                              check.employer?.profile?.companyName ||
                              'Employer'}
                          </strong>
                        </p>
                        <p className="background-check-scope">
                          Scope:{' '}
                          {Array.isArray(check.scope)
                            ? check.scope.join(', ')
                            : check.scope || 'Not specified'}
                        </p>
                        <p className="background-check-dates">
                          Requested:{' '}
                          {check.createdAt
                            ? new Date(
                                check.createdAt
                              ).toLocaleDateString()
                            : 'Unknown'}
                        </p>
                      </div>

                      <div className="background-check-status">
                        <span
                          className={getBackgroundStatusBadgeClass(
                            check.status
                          )}
                        >
                          {check.status || 'PENDING'}
                        </span>
                        {check.result && (
                          <span
                            className={getBackgroundResultBadgeClass(
                              check.result
                            )}
                            style={{ marginLeft: '0.5rem' }}
                          >
                            {check.result}
                          </span>
                        )}
                      </div>

                      {check.status === 'PENDING_CONSENT' && (
                        <div className="background-check-actions">
                          <button
                            className="btn-primary"
                            disabled={loadingBackgroundCheck}
                            onClick={() => handleGiveConsent(check.id)}
                          >
                            Give Consent
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <div className="dashboard-sidebar">
            
            {/* Profile Info Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>👤 Profile Info</h3>
                {/*Edit Profile Button */}
                <Link 
                  to="/candidate/profile" 
                  style={{ 
                    fontSize: '0.875rem',
                    color: '#3b82f6',
                    textDecoration: 'none',
                    fontWeight: '500'
                  }}
                >
                  Edit Profile
                </Link>
              </div>
              <div className="profile-info">
                <div className="info-row">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{getDisplayName()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{getDisplayEmail()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{getDisplayPhone()}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Role:</span>
                  <span className="verification-badge validated">Candidate</span>
                </div>
              </div>
            </div>

            {/* MFA Settings Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>🔒 Security Settings</h3>
              </div>
              <div className="mfa-settings">
                <div className="mfa-status">
                  <div className="mfa-icon">
                    {mfaSettings.mfaEnabled ? '🔒' : '🔓'}
                  </div>
                  <div className="mfa-info">
                    <h4>Two-Factor Authentication</h4>
                    <p style={{ fontSize: '0.875rem', color: '#666', margin: '0.25rem 0 0 0' }}>
                      {mfaSettings.mfaEnabled 
                        ? `Enabled via ${mfaSettings.mfaType}`
                        : 'Add an extra layer of security to your account'}
                    </p>
                  </div>
                </div>
                <button 
                  className="btn-primary"
                  onClick={() => setShowMFASettings(true)}
                  style={{
                    width: '100%',
                    marginTop: '1rem',
                    padding: '0.75rem',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  {mfaSettings.mfaEnabled ? 'Manage MFA' : 'Enable MFA'}
                </button>
              </div>
            </div>

            {/* Quick Actions Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>⚡ Quick Actions</h3>
              </div>
              <div className="action-buttons">
                <Link to="/candidate/profile" className="action-btn">
                  <span className="action-icon">👤</span>
                  <span>Edit Profile</span>
                </Link>
                <Link to="/candidate/sections" className="action-btn">
                  <span className="action-icon">📚</span>
                  <span>Add Record</span>
                </Link>
                <Link to="/candidate/background-check" className="action-btn">
                  <span className="action-icon">🔍</span>
                  <span>Background Check</span>
                </Link>
                <Link to="/change-password" className="action-btn">
                  <span className="action-icon">🔒</span>
                  <span>Change Password</span>
                </Link>
                <button onClick={handleLogout} className="action-btn logout-btn">
                  <span className="action-icon">🚪</span>
                  <span>Logout</span>
                </button>
              </div>
            </div>

            {/* Verification Progress Card */}
            <div className="dashboard-card">
              <div className="card-header">
                <h3>📊 Verification Progress</h3>
              </div>
              <div className="verification-progress">
                <div className="progress-stats">
                  <div className="progress-item">
                    <span>Verified</span>
                    <span className="progress-count verified">{stats.verified}</span>
                  </div>
                  <div className="progress-item">
                    <span>Pending</span>
                    <span className="progress-count pending">{stats.pending}</span>
                  </div>
                  <div className="progress-item">
                    <span>Total</span>
                    <span className="progress-count">{stats.total}</span>
                  </div>
                </div>
                <div className="overall-progress">
                  <div className="progress-bar-container">
                    <div className="progress-bar-fill" style={{ width: `${stats.completionPercentage}%` }}></div>
                  </div>
                  <p className="progress-percentage">{stats.completionPercentage}% Complete</p>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>

      {/* MFA Settings Modal */}
            {/*Simpler MFA Settings Modal*/}
      {showMFASettings && (
        <div className="dashboard-modal-overlay" onClick={() => setShowMFASettings(false)}>
          <div className="dashboard-modal-content mfa-simple-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dashboard-modal-header">
              <h2>Configure Two-Factor Authentication</h2>
              <button className="dashboard-close-btn" onClick={() => setShowMFASettings(false)}>×</button>
            </div>

            <div className="dashboard-modal-body">
              {/* MFA Type Selection */}
              <div className="mfa-type-options-simple">
                <label className={`mfa-type-card ${tempSettings.mfaType === 'email' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="email"
                    checked={tempSettings.mfaType === 'email'}
                    onChange={() => handleMfaTypeChange('email')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📧</div>
                    <div className="card-text">
                      <h4>Email Verification</h4>
                      <p>Send verification codes to your email</p>
                    </div>
                  </div>
                </label>

                <label className={`mfa-type-card ${tempSettings.mfaType === 'sms' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="mfaType"
                    value="sms"
                    checked={tempSettings.mfaType === 'sms'}
                    onChange={() => handleMfaTypeChange('sms')}
                  />
                  <div className="card-content">
                    <div className="card-icon">📱</div>
                    <div className="card-text">
                      <h4>SMS Verification</h4>
                      <p>Send verification codes to your phone</p>
                    </div>
                  </div>
                </label>
              </div>

              {/* Enable MFA Button */}
              <button
                className="btn-enable-mfa"
                onClick={handleMFAToggle}
                disabled={loading}
              >
                <span className="btn-icon">🔒</span>
                {loading ? 'Processing...' : mfaSettings.mfaEnabled ? 'Update MFA' : 'Enable MFA'}
              </button>

              {/* Optional: Disable MFA Button */}
              {mfaSettings.mfaEnabled && (
                <button
                  className="btn-disable-mfa"
                  onClick={handleMFAToggle}
                  disabled={loading}
                >
                  Disable MFA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default CandidateDashboard;
